import Button from '@/shared/Button'
import FormAutocomplete from '@/shared/FormCreator/FormItem/FormAutocomplete'

import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'

import Box from '@mui/material/Box'

import HighlightOff from '@mui/icons-material/HighlightOff'
import IconButton from '@mui/material/IconButton'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import debounce from '@mui/material/utils/debounce'

import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import unionBy from 'lodash/unionBy'

const mapStateToProps = loading => ({
  isLandingPageLabelsLoading: loading['landingpage/getLandingPageLabels'],
  isLandingPageAddableLabelsLoading: loading['landingpage/getLandingPageAddableLabels'],
})

const Selection = ({ data, onRemove }) => (
  <Chip
    icon={
      <IconButton
        size="small"
        sx={{ padding: 0 }}
        onClick={() => {
          onRemove(data?.id)
        }}
      >
        <HighlightOff />
      </IconButton>
    }
    label={data?.label}
  />
)

const EditLandingPageLabels = ({
  dispatch,
  isLoading,
  onClose,
  onComplete,
  data,
  type,
  isEdit,
  landingpageTempId = null,
  isLandingPageLabelsLoading,
  isLandingPageAddableLabelsLoading,
  ...props
}) => {
  const { t } = useTranslation()

  const searchInputRef = useRef()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState({})
  const [selected, setSelected] = useState([])
  const [page, setPage] = useState(0)

  const { data: searchResultsData = [], paging } = searchResults

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  const handleClose = () => {
    setSelected([])
    setSearchInput('')
    setSearchResults({})
    onClose?.()
  }

  const handleSubmit = () => {
    onComplete?.(selected)
    handleClose?.()
  }

  const handleSelectionRemove = id => {
    setSelected(selected.filter(s => s.id !== id))
  }

  const handleAddLabel = () => {
    const existing = searchResultsData.find(
      res => res.label.toLowerCase() === searchInputRef.current.toLowerCase(),
    )
    if (existing) {
      setSelected([existing, ...selected])
    } else {
      const currentValue = searchInputRef.current.toLowerCase()
      setSelected([{ id: currentValue, label: currentValue }, ...selected])
    }
  }

  const fetchData = useCallback(() => {
    let dispatchType
    let payload = { type: type === 'system' ? 'S' : 'I', pageIndex: page, pageSize: 100 }
    if (isEdit) {
      dispatchType = 'landingPage/getLandingPageAddableLabels'
    } else {
      dispatchType = 'landingPage/getLandingPageLabels'
    }

    if (landingpageTempId) {
      // payload = { ...payload, landingpageTempId, pageIndex: 0, pageSize: 10, searchLabelName: '' };
      payload = { ...payload, landingpageTempId }
    }

    if (searchInputRef.current) {
      payload = { ...payload, searchLabelName: searchInputRef.current }
    }
    dispatch({
      type: dispatchType,
      payload,
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.landingpageLabelId, label: d.labelName }))

        setSearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, isEdit, landingpageTempId, type, page])

  const fetchDataDebounce = useMemo(
    () =>
      debounce(() => {
        fetchData()
      }, 400),
    [fetchData],
  )

  useEffect(() => {
    fetchDataDebounce()
  }, [searchInput, page, fetchDataDebounce])

  useEffect(() => {
    if (data) {
      setSelected(data)
    }
  }, [data])

  // NOTE: searchResults might have exactly same label and AutoComplete is using label as key
  return (
    <Dialog {...props} maxWidth={'md'}>
      <DialogTitle>{t('general.edit_label')}</DialogTitle>
      <DialogContent sx={{ width: 500 }}>
        <Stack flexDirection={'row'} gap={1} mb={2} alignItems={'center'}>
          <Typography>{`${t('general.label_name')}:`}</Typography>
          <Typography color={theme => theme.palette.grey_60.main}>
            {t('general.label_info')}
          </Typography>
        </Stack>
        <Stack mb={10} direction="row" justifyContent="flex-start">
          <Box>
            <FormAutocomplete
              freeSolo
              inputConfig={{
                onInputChange: (_, value, reason) => {
                  searchInputRef.current = value

                  if (['input', 'clear', 'reset'].includes(reason)) {
                    setPage(0)
                    setSearchInput(value)
                  }
                },
                onLoadMore: () => {
                  const { hasNext } = paging

                  if (
                    ((!isEdit && !isLandingPageLabelsLoading) ||
                      (isEdit && !isLandingPageAddableLabelsLoading)) &&
                    hasNext
                  ) {
                    loadMoreDebounce()
                  }
                },
              }}
              options={searchResultsData}
              getOptionDisabled={option => selected.some(s => s.id === option.id)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onFocus={fetchDataDebounce}
            />
          </Box>
          <Button
            sx={{ ml: 2, borderColor: theme => theme.palette.btn_positive.main }}
            variant="outlined"
            color="btn_positive"
            disabled={
              !searchInput ||
              selected
                .map(result => result.label?.toLowerCase())
                .includes(searchInput?.toLowerCase())
            }
            onClick={() => {
              handleAddLabel()
            }}
          >
            {t('general.add')}
          </Button>
        </Stack>

        <Card variant="outlined">
          <CardContent sx={{ minHeight: 100 }}>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {selected.map(obj => (
                <Selection key={obj?.id} data={obj} onRemove={handleSelectionRemove} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button color={'white'} loading={isLoading} disabled={isLoading} onClick={handleClose}>
          {t('general.cancel')}
        </Button>
        <Button
          color={'btn_positive'}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(EditLandingPageLabels)
